import React, { useRef, useEffect } from "react";
import PropTypes from "prop-types";
import { useFocusTrap } from "../../../hooks/useFocusTrap";

export function ModernizedDismissibleButton({ onClick, label }) {
  return (
    <button
      type='button'
      aria-label={label}
      onClick={onClick}
      className='modernized-dismissible-button'
      data-testid='modernized-dismissible-button'
    />
  );
}
export function ModernizedModalOverlay({
  meta = {},
  children,
  dialogOverlay = true,
  overlayClass = ""
}) {
  const dataAttrs = Object.fromEntries(
    Object.entries(meta).map(([key, value]) => {
      return [`data-${key}`, value];
    })
  );

  return dialogOverlay ? (
    <div
      {...dataAttrs}
      className={`modernized-modal-overlay-web-sdk ${overlayClass}`}
      data-testid='modernized-modal-overlay'
    >
      {children}
    </div>
  ) : (
    <>{children}</>
  );
}

export function ModernizedModalBody({
  children,
  bodyClass,
  handleClickOutside,
  ...dialogAriaProps
}) {
  const modalwRef = useRef(null);
  useFocusTrap(modalwRef);

  return (
    <div
      ref={modalwRef}
      role='presentation'
      onClick={handleClickOutside}
      className={`modernized-modal-wrapper-web-sdk ${bodyClass}`}
      {...dialogAriaProps}
    >
      <div
        role='presentation'
        onClick={event => event.stopPropagation()}
        onKeyDown={ev => {
          if (ev.key === "Escape") {
            handleClickOutside();
          }
        }}
        className='modernized-modal-web-sdk'
        data-testid='modernized-modal-body'
      >
        {children}
      </div>
    </div>
  );
}

export function ModernizedModalHeaderContainer({ children, headerClass }) {
  return (
    <div className={`modernized-modal-header-web-sdk ${headerClass}`}>
      {children}
    </div>
  );
}

export function ModernizedModalHeader({ id, children }) {
  const headerRef = useRef(null);

  useEffect(() => {
    headerRef.current.focus();
  }, []);

  return (
    <h2
      ref={headerRef}
      id={id}
      tabIndex='-1'
      data-testid='modernized-modal-header'
    >
      {children}
    </h2>
  );
}

export function ModernizedModalContent({ children }) {
  return (
    <div
      className='modernized-modal-content-web-sdk'
      data-testid='modernized-modal-content'
    >
      <p>{children}</p>
    </div>
  );
}

export function ModernizedModalFooter({ children, footerClass }) {
  return (
    <div className={`modernized-modal-footer-web-sdk ${footerClass}`}>
      {children}
    </div>
  );
}

ModernizedDismissibleButton.propTypes = {
  label: PropTypes.string.isRequired,
  onClick: PropTypes.func.isRequired
};

ModernizedModalOverlay.propTypes = {
  meta: PropTypes.objectOf(PropTypes.string),
  children: PropTypes.node.isRequired,
  dialogOverlay: PropTypes.bool,
  overlayClass: PropTypes.string
};

ModernizedModalBody.propTypes = {
  children: PropTypes.node.isRequired,
  bodyClass: PropTypes.string,
  handleClickOutside: PropTypes.func.isRequired
};

ModernizedModalHeaderContainer.propTypes = {
  children: PropTypes.node,
  headerClass: PropTypes.string
};

ModernizedModalHeader.propTypes = {
  id: PropTypes.string,
  children: PropTypes.node.isRequired
};

ModernizedModalContent.propTypes = {
  children: PropTypes.node.isRequired
};

ModernizedModalFooter.propTypes = {
  children: PropTypes.node.isRequired,
  footerClass: PropTypes.string
};
